import { useSyncExternalStore } from 'react'

/*
 * O que a busca grava na URL da rota do mapa: `?municipio=<código IBGE>` ou
 * `?recibo=<recibo do CAR>`, um excluindo o outro. A URL é a fonte do recorte:
 * recarregar a página ou mandar o link reaplica a busca.
 */
export interface BuscaNaUrl {
  municipio?: number
  recibo?: string
}

/** Código de município do IBGE: sete algarismos, nunca começando por zero. */
const CODIGO_DO_MUNICIPIO = /^[1-9]\d{6}$/

/**
 * Lê a busca de uma URL qualquer e descarta o que não faz sentido (município que
 * não é código de sete algarismos, recibo vazio) em vez de quebrar a tela. O
 * roteador já converte `?municipio=3301009` em número e deixa o resto como
 * texto, então os dois formatos entram aqui. Município e recibo juntos não
 * existem: vale o município.
 */
export function validarBusca(bruto: Record<string, unknown>): BuscaNaUrl {
  const municipio = bruto.municipio
  if (
    (typeof municipio === 'number' || typeof municipio === 'string') &&
    CODIGO_DO_MUNICIPIO.test(String(municipio))
  ) {
    return { municipio: Number(municipio) }
  }

  const recibo = bruto.recibo
  if (typeof recibo === 'string' || typeof recibo === 'number') {
    const texto = String(recibo).trim()
    if (texto !== '') return { recibo: texto }
  }
  return {}
}

/*
 * Busca repetida. Buscar de novo o que já está na URL não muda a URL, e quem
 * reage à busca (o mapa) só vê a URL; este contador é o sinal de que a pessoa
 * pediu a mesma busca outra vez, para o mapa voltar ao município ou ao imóvel
 * e o aviso fechado reaparecer.
 */
let buscasRepetidas = 0
const ouvintes = new Set<() => void>()

export function repetirBusca(): void {
  buscasRepetidas += 1
  for (const ouvinte of ouvintes) ouvinte()
}

/** Quantas vezes a pessoa repetiu a busca da URL; só a diferença entre leituras importa. */
export function useBuscasRepetidas(): number {
  return useSyncExternalStore(
    (aviso) => {
      ouvintes.add(aviso)
      return () => ouvintes.delete(aviso)
    },
    () => buscasRepetidas
  )
}
